import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Sparkles, Clock } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

export default function PromoBanner() {
  const [dismissed, setDismissed] = useState(false);
  const [daysLeft, setDaysLeft] = useState(0);
  const { user } = useAuth();
  
  useEffect(() => {
    // Check if banner was dismissed this session
    const wasDismissed = sessionStorage.getItem('promo_banner_dismissed');
    if (wasDismissed) {
      setDismissed(true);
    }
    
    // Calculate days left until end of month
    const now = new Date();
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const diffTime = endOfMonth - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    setDaysLeft(diffDays);
  }, []);
  
  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('promo_banner_dismissed', 'true');
  };
  
  // Don't show for subscribed users or if dismissed
  if (dismissed || user?.subscription_tier === 'premium' || user?.subscription_tier === 'ultra') {
    return null;
  }
  
  return (
    <div 
      className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white relative overflow-hidden"
      data-testid="promo-banner"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-4 -left-4 w-24 h-24 bg-white/10 rounded-full blur-2xl animate-pulse" />
        <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-white/10 rounded-full blur-2xl animate-pulse delay-300" />
      </div>
      
      <div className="container mx-auto px-4 py-3 relative">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="hidden sm:flex items-center justify-center w-10 h-10 bg-white/20 rounded-full flex-shrink-0">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="font-bold text-sm sm:text-base whitespace-nowrap">
                  50% OFF
                </span>
                <span className="text-white/90 text-sm hidden sm:inline">
                  Premium subscription - Sign up before the end of the month!
                </span>
                <span className="text-white/90 text-sm sm:hidden">
                  Premium - This month only!
                </span>
              </div>
              <div className="flex items-center gap-1 text-white/80 text-xs mt-0.5">
                <Clock className="h-3 w-3" />
                <span>{daysLeft} day{daysLeft !== 1 ? 's' : ''} left</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link
              to="/subscribe"
              className="bg-white text-orange-600 px-4 py-1.5 rounded-full text-sm font-semibold hover:bg-orange-50 transition-colors whitespace-nowrap"
              data-testid="promo-banner-cta"
            >
              Claim Offer
            </Link>
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Dismiss banner"
              data-testid="promo-banner-dismiss"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
